import { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Avatar } from '../primitives';
import { useAuth } from '../../features/auth/AuthContext';
import { StavbaSwitcher } from './StavbaSwitcher';
import { Icon } from '../icons/Icon';
import { navSections, mobileNavSections, sectionForPath, type NavItem } from './navigation';
import styles from './Sidebar.module.css';
import { cx } from '../../lib/cx';
import { useObnovaCount } from '../../features/zamestnanci/obnova';
import { useVyuctovaniUpozorneniCount } from '../../features/vyuctovani/upozorneni';
import { useKosilkyKeSchvaleniCount } from '../../features/documents/kosilkaBadge';

const COLLAPSED_KEY = 'sd-nav-collapsed';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* ignore */
  }
  // Výchozí stav: všechny kapitoly sbalené (dokud si uživatel nezvolí jinak)
  return new Set(navSections.map((s) => s.id));
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const obnovaCount = useObnovaCount();
  const prevodyCount = useVyuctovaniUpozorneniCount();
  const kosilkyCount = useKosilkyKeSchvaleniCount();
  const location = useLocation();
  // null = zavřeno, 'all' = celé menu („Více"), jinak id sekce (nabídka jednoho oddílu)
  const [sheet, setSheet] = useState<string | null>(null);
  const menuOpen = sheet !== null;
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const toggleSection = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const badgeFor = (key?: string) => {
    if (key === 'obnova') return obnovaCount > 0 ? obnovaCount : null;
    if (key === 'prevody') return prevodyCount > 0 ? prevodyCount : null;
    if (key === 'kosilky') return kosilkyCount > 0 ? kosilkyCount : null;
    return null;
  };
  const displayName = user?.full_name || user?.email || 'Nepřihlášen';
  const initials = (user?.full_name || user?.email || '??').slice(0, 2).toUpperCase();

  const activeSection = sectionForPath(location.pathname);
  const sectionHasBadge = (id: string) =>
    navSections.find((s) => s.id === id)?.items.some((i) => badgeFor(i.badgeKey) != null) ?? false;
  const toggleSheet = (id: string) => setSheet((cur) => (cur === id ? null : id));
  const sheetSection = sheet && sheet !== 'all' ? navSections.find((s) => s.id === sheet) : undefined;

  // Zavřít vysouvací menu po přechodu na jinou stránku
  useEffect(() => {
    setSheet(null);
  }, [location.pathname]);

  // Zamknout scroll pozadí, když je menu otevřené
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const renderItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/app'}
      onClick={() => setSheet(null)}
      className={({ isActive }) => cx(styles.item, isActive && styles.active)}
    >
      <span className={styles.icon} aria-hidden>
        {item.icon}
      </span>
      {item.label}
      {badgeFor(item.badgeKey) != null && (
        <span
          aria-label={`${badgeFor(item.badgeKey)} ${item.badgeKey === 'prevody' ? 'převodů ke kontrole' : item.badgeKey === 'kosilky' ? 'košilek ke schválení' : 'k obnově'}`}
          className={styles.navBadge}
        >
          {badgeFor(item.badgeKey)}
        </span>
      )}
    </NavLink>
  );

  const sectionsNav = navSections.map((section) => {
    const isCollapsed = collapsed.has(section.id);
    return (
    <div key={section.id} className={styles.section}>
      <button
        type="button"
        className={styles.sectionLabel}
        onClick={() => toggleSection(section.id)}
        aria-expanded={!isCollapsed}
      >
        <span className={cx(styles.sectionChevron, isCollapsed && styles.sectionChevronCollapsed)} aria-hidden>
          ▾
        </span>
        {section.label}
      </button>
      {!isCollapsed && section.items.map(renderItem)}
    </div>
    );
  });

  return (
    <nav className={styles.sidebar} aria-label="Hlavní navigace">
      {/* Desktop: full nav */}
      <Link to="/app" className={styles.brand} title="Zpět na rozcestník" aria-label="Zpět na rozcestník">
        <div className={styles.logo}>S</div>
        <div className={styles.brandText}>
          <span className={styles.brandName}>SiteDoc</span>
        </div>
      </Link>

      <StavbaSwitcher />

      <div className={styles.scrollArea}>{sectionsNav}</div>

      <div className={styles.footer}>
        <Avatar initials={initials} size="sm" current />
        <div className={styles.footerText}>
          <span className={styles.footerName}>{displayName}</span>
          <span className={styles.footerRole}>{user?.is_admin ? 'admin' : user?.can_zapis_kd ? 'stavbyvedoucí' : 'uživatel'}</span>
        </div>
        <button type="button" className={styles.logoutBtn} onClick={logout}>
          Odhlásit
        </button>
      </div>

      {/* Mobile: spodní lišta = oddíly (klik → nabídka oddílu) + „Více" otevírá plné menu.
          Rozcestník tu není — vede na něj logo SiteDoc v horní liště. */}
      <div className={styles.mobileNav}>
        {mobileNavSections.map((section) => {
          const open = sheet === section.id;
          return (
            <button
              key={section.id}
              type="button"
              className={cx(
                styles.mobileItem,
                (open || (sheet === null && activeSection === section.id)) && styles.active,
              )}
              onClick={() => toggleSheet(section.id)}
              aria-expanded={open}
              aria-label={section.label}
            >
              <span className={styles.mobileItemIcon} aria-hidden>
                <Icon name={section.mobile!.icon} size={22} />
              </span>
              {section.mobile!.label}
              {sectionHasBadge(section.id) && <span className={styles.mobileDot} aria-hidden />}
            </button>
          );
        })}
        <button
          type="button"
          className={cx(styles.mobileItem, sheet === 'all' && styles.active)}
          onClick={() => toggleSheet('all')}
          aria-expanded={sheet === 'all'}
          aria-label="Všechny moduly"
        >
          <span className={styles.mobileItemIcon} aria-hidden>
            <Icon name="list" size={22} />
          </span>
          Více
        </button>
      </div>

      {/* Mobile: nabídka jednoho oddílu */}
      {sheetSection && (
        <div className={styles.drawerRoot} role="dialog" aria-modal="true" aria-label={sheetSection.label}>
          <div className={styles.drawerBackdrop} onClick={() => setSheet(null)} />
          <div className={cx(styles.drawerPanel, styles.sheetPanel)}>
            <div className={styles.drawerHeader}>
              <span className={styles.sheetTitle}>{sheetSection.label}</span>
              <button
                type="button"
                className={styles.drawerClose}
                onClick={() => setSheet(null)}
                aria-label="Zavřít"
              >
                ✕
              </button>
            </div>
            <div className={styles.drawerScroll}>
              <div className={styles.section}>{sheetSection.items.map(renderItem)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: vysouvací menu se všemi moduly */}
      {sheet === 'all' && (
        <div className={styles.drawerRoot} role="dialog" aria-modal="true" aria-label="Navigace">
          <div className={styles.drawerBackdrop} onClick={() => setSheet(null)} />
          <div className={styles.drawerPanel}>
            <div className={styles.drawerHeader}>
              <Link to="/app" className={styles.brand} aria-label="Zpět na rozcestník">
                <div className={styles.logo}>S</div>
                <div className={styles.brandText}>
                  <span className={styles.brandName}>SiteDoc</span>
                </div>
              </Link>
              <button
                type="button"
                className={styles.drawerClose}
                onClick={() => setSheet(null)}
                aria-label="Zavřít"
              >
                ✕
              </button>
            </div>

            <StavbaSwitcher variant="drawer" />

            <div className={styles.drawerScroll}>{sectionsNav}</div>

            <div className={styles.footer}>
              <Avatar initials={initials} size="sm" current />
              <div className={styles.footerText}>
                <span className={styles.footerName}>{displayName}</span>
                <span className={styles.footerRole}>
                  {user?.is_admin ? 'admin' : user?.can_zapis_kd ? 'stavbyvedoucí' : 'uživatel'}
                </span>
              </div>
              <button type="button" className={styles.logoutBtn} onClick={logout}>
                Odhlásit
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
